import React from 'react';
import { styled } from 'baseui';
import { Block } from 'baseui/block';
import { PLACEMENT, toaster, ToasterContainer } from 'baseui/toast';
import globalWindow from 'global/window';
import type { OutputLabel } from './';
import EditorCanvas, { type CanvasOffset, type ImageSize, type MoveDirection, type ReferenceMouseEvent, LineType } from './editor-canvas.react';
import { Panel } from './panel.react';
import { ToolBar } from './toolbar.react';
import type { LabelValidationT } from './types';
import { calculateBboxForResize, rotateAnnotationsByNinetyDegree } from './utils';
export const MODES = {
  append: 'append',
  pan: 'pan',
  appending: 'appending',
  select: 'select',
  resize: 'resize',
  move: 'move'
};
export const Labels: {
  [x: string]: string;
} = {
  box: 'boundingBox',
  ellipse: 'boundingEllipse'
};
const GRID_MODES = 3;
// off, gray, black

export type Label = {
  title: string;
  types: ['rect'];
  max?: number;
  isRequired?: boolean;
};
export type BoundingBoxTool = {
  type: 'boundingBox';
  title: string;
  description: string;
  color: string;
  max?: number;
  isRequired?: boolean;
};
export type ImageMetadata = {
  imageSize: {
    width: number;
    height: number;
  };
  rotation?: number;
  invalidImage: boolean;
  labelValidation: LabelValidationT;
};
export type Annotation = {
  toolIndex: number;
  meta: {
    x: number;
    y: number;
    width: number;
    height: number;
    drawMode?: LabelType;
  };
  categoryName: string;
  isInitial?: boolean;
  max?: number;
  isRequired?: boolean;
};
export type InitialState = {
  annotations?: ReadonlyArray<OutputLabel>;
  rotation?: number;
  invalidImage?: boolean;
  selectedLabelTypes?: LabelType[];
};
export type Props = {
  imageId: string;
  isRotateEnabled: boolean;
  imageUrl?: string | null;
  tools: BoundingBoxTool[];
  onSubmit: (b: OutputLabel[], a: ImageMetadata) => void;
  extrudeWidth: number;
  initialState: InitialState;
  showError?: (message: string) => void;
};
type Mode = keyof typeof MODES;
export type LabelType = (typeof Labels)[keyof typeof Labels];
type State = {
  selectedIndex: number;
  annotations: Annotation[];
  currentAnnotation: Annotation | undefined | null;
  scale: number;
  rotation: number;
  offsets: {
    x: number;
    y: number;
  };
  imgSize: {
    width: number;
    height: number;
  };
  isDragging: boolean;
  isPanning: boolean;
  dragStartX: number;
  dragStartY: number;
  canvasWidth: number;
  mode: Mode;
  isMouseDown: boolean;
  hiddenToolIds: number[];
  gridMode: number;
  selectedAnnotation: number;
  direction: MoveDirection;
  invalidImage: boolean;
  labelValidation: LabelValidationT;
  appendMode: LabelType;
  selectedLabelTypes: LabelType[];
};
const TOOLBAR_WIDTH = 50;
const PANEL_WIDTH = 300;
const Container = styled<typeof Block, {}>(Block, ({
  $theme
}) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  color: $theme.colors.contentSecondary
}));
const ToolbarContainer = styled('div', ({
  $theme
}) => ({
  backgroundColor: $theme.colors.backgroundSecondary,
  padding: '16px 5px',
  top: 0,
  left: 0,
  position: 'absolute'
}));
class ImageLabel extends React.Component<Props, State> {
  static displayName = 'ImageLabel';
  static defaultProps = {
    isRotateEnabled: false,
    initialState: {
      annotations: [],
      rotation: 0,
      invalidImage: false,
      selectedLabelTypes: [Labels.box]
    }
  };
  containerRef = React.createRef<HTMLDivElement | undefined | null>();
  resizeObserver: ResizeObserver | undefined | null;
  constructor(props: Props) {
    super(props);
    const initialState = {
      ...ImageLabel.defaultProps.initialState,
      ...props.initialState
    };
    this.state = {
      selectedIndex: -1,
      // @ts-expect-error missing null check
      annotations: initialState.annotations.map(a => ({
        ...a,
        isInitial: true
      })),
      dragStartX: 0,
      dragStartY: 0,
      currentAnnotation: null,
      scale: 1,
      rotation: initialState.rotation,
      offsets: {
        x: 0,
        y: 0
      },
      imgSize: {
        width: -1,
        height: -1
      },
      isPanning: false,
      isDragging: false,
      canvasWidth: this.getCanvasWidth(globalWindow.innerWidth),
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      mode: MODES.append,
      isMouseDown: false,
      hiddenToolIds: [],
      gridMode: 1,
      selectedAnnotation: -1,
      direction: 'top',
      invalidImage: initialState.invalidImage,
      labelValidation: {
        isValid: true,
        reasons: []
      },
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string | undefined' is not assignable to type 'string'.
      appendMode: Labels.box,
      // @ts-expect-error todo(ts-migration) TS2322 Type '(string | undefined)[]' is not assignable to type 'string[]'.
      selectedLabelTypes: initialState.selectedLabelTypes
    };
  }
  componentDidMount() {
    globalWindow.addEventListener('keydown', this.handleKeydown);
    globalWindow.addEventListener('keyup', this.handleKeyup);
    globalWindow.addEventListener('resize', this.handleResize);
    this.resizeObserver = new ResizeObserver(entries => {
      this.updateCanvasSize();
    });
    this.containerRef.current && this.resizeObserver.observe(this.containerRef.current);
    this.updateCanvasSize();
    this.submit();
  }
  componentWillUnmount() {
    globalWindow.removeEventListener('resize', this.handleResize);
    globalWindow.removeEventListener('keydown', this.handleKeydown);
    globalWindow.removeEventListener('keyup', this.handleKeyup);
    this.resizeObserver?.disconnect();
  }
  componentDidUpdate() {
    this.updateCanvasSize();
  }
  updateCanvasSize() {
    const {
      canvasWidth
    } = this.state;
    const newCanvasWidth = this.containerRef.current ? this.getCanvasWidth(this.containerRef.current.getBoundingClientRect().width) : 0;
    if (newCanvasWidth !== canvasWidth) {
      this.setState({
        canvasWidth: newCanvasWidth
      });
    }
  }
  getCanvasWidth(width: number) {
    return width - PANEL_WIDTH - TOOLBAR_WIDTH - this.props.extrudeWidth;
  }
  handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Alt' && this.state.mode === MODES.append) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        mode: MODES.pan
      });
    }
    if (e.shiftKey) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        mode: MODES.select
      });
    }
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z') {
        // allow ctrl or command (meta) to support both
        // windows and mac
        this.undo();
      }
    }
    if (e.key === 'Delete') {
      this.deleteAnnotation();
    }
    if (e.key === 'g') {
      this.toggleGridMode();
    }
    if (e.key === 'Escape') {
      // clear out of the selected indx so that
      // user can move mouse freely.
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        selectedIndex: -1,
        selectedAnnotation: -1,
        mode: MODES.append
      });
    }
  };
  deleteAnnotation = () => {
    const {
      selectedAnnotation,
      annotations,
      mode
    } = this.state;
    // Delete only when the mode is Select and there is a selected Annotation
    if (selectedAnnotation !== -1 && mode === MODES.select) {
      annotations.splice(selectedAnnotation - 1, 1);
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        annotations,
        mode: MODES.append,
        selectedAnnotation: -1
      }, this.submit);
    }
  };
  onAnnotationClick = (e: ReferenceMouseEvent, idx: number) => {
    const {
      mode,
      selectedAnnotation
    } = this.state;
    if (mode === MODES.select) {
      this.setState({
        selectedAnnotation: idx
      });
    }
    if (mode === MODES.select && selectedAnnotation === idx) {
      this.setState({
        // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
        mode: MODES.move,
        dragStartX: e.referenceX,
        dragStartY: e.referenceY
      });
    }
  };
  onVertexClick = (e: ReferenceMouseEvent, direction: MoveDirection) => {
    this.setState({
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      mode: MODES.resize,
      dragStartX: e.referenceX,
      dragStartY: e.referenceY,
      direction
    });
  };
  handleKeyup = (e: KeyboardEvent) => {
    if (e.key === 'Alt' && this.state.mode === MODES.pan) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        mode: MODES.append
      });
    }
  };
  handleRotate = () => {
    // rotate image 90 degrees clock-wised
    const {
      rotation,
      imgSize,
      annotations
    } = this.state;
    const newRotation = (rotation + 90) % 360;
    this.setState({
      rotation: newRotation,
      annotations: rotateAnnotationsByNinetyDegree(newRotation, imgSize, annotations)
    });
  };
  handleResize = () => {
    this.setState({
      canvasWidth: this.getCanvasWidth(globalWindow.innerWidth)
    });
  };
  handleMouseDown = (e: ReferenceMouseEvent) => {
    this.setState({
      isMouseDown: true
    });
    if (this.state.mode === MODES.append) {
      this.startAppending(e);
      return;
    }
  };
  showError = (message: string) => {
    toaster.negative(<>{message}</>, {
      autoHideDuration: 5000
    });
  };
  startAppending = (e: ReferenceMouseEvent) => {
    if (this.state.selectedIndex < 0) {
      return;
    }

    // if we have got allowed max bounding boxes and operator is attempting to add
    // more than that block it and show the error
    if (this.isAnnotationReachedMax(this.state.selectedIndex)) {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      const label = this.props.tools[this.state.selectedIndex].title;

      // use showError if provided in props else fallback to local error notification
      const showError = this.props.showError || this.showError;
      showError(`Maximum bounding box allowed for label '${label}' reached`);
      return;
    }

    // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
    this.setState({
      dragStartX: e.referenceX,
      dragStartY: e.referenceY,
      mode: MODES.appending
    });
  };
  handleMouseMove = (e: ReferenceMouseEvent) => {
    if (this.state.mode === MODES.appending) {
      this.onAppendingDrag(e);
      return;
    }
    if (this.state.mode === MODES.move) {
      this.onTranslating(e);
      return;
    }
    if (this.state.mode === MODES.resize) {
      this.onResizing(e);
      return;
    }
    if (this.state.mode === MODES.pan && this.state.isMouseDown) {
      this.onPan(e);
      return;
    }
  };
  handleUpdateImageSize = (imgSize: ImageSize) => {
    this.setState({
      imgSize
    }, this.submit);
  };
  onPan = (e: ReferenceMouseEvent) => {
    this.adjustOffset(e.referenceMovementX, e.referenceMovementY);
  };
  onResizing = (e: ReferenceMouseEvent) => {
    const {
      selectedAnnotation,
      annotations,
      direction
    } = this.state;
    const annotation = annotations[selectedAnnotation - 1];
    if (!annotation) {
      // Do nothing if annotation is not found
      return;
    }
    const updatedMeta = calculateBboxForResize([e.referenceX, e.referenceY], annotation.meta, direction);
    annotation.meta = {
      ...annotation.meta,
      ...updatedMeta
    };
    delete annotation.isInitial;
    this.setState({
      annotations
    });
  };
  onTranslating = (e: ReferenceMouseEvent) => {
    const {
      selectedAnnotation,
      annotations,
      offsets,
      dragStartX,
      dragStartY
    } = this.state;
    const annotation = annotations[selectedAnnotation - 1];
    if (!annotation) {
      // Do nothing if annotation is not found
      return;
    }
    annotation.meta.x = e.referenceX - dragStartX - offsets.x;
    annotation.meta.y = e.referenceY - dragStartY - offsets.y;
    delete annotation.isInitial;
    this.setState({
      annotations
    });
  };
  onAppendingDrag = (e: ReferenceMouseEvent) => {
    // Currently only supports the boundingBox. Will have to be
    // refactored for other tools.
    const x = Math.min(this.state.dragStartX, e.referenceX);
    const y = Math.min(this.state.dragStartY, e.referenceY);
    const height = Math.abs(this.state.dragStartY - e.referenceY);
    const width = Math.abs(this.state.dragStartX - e.referenceX);
    const box = {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      categoryName: this.props.tools[this.state.selectedIndex].title,
      toolIndex: this.state.selectedIndex,
      meta: {
        x,
        y,
        height,
        width
      }
    };
    this.setState({
      currentAnnotation: box
    });
  };
  handleMouseUp = (e: ReferenceMouseEvent) => {
    this.setState({
      isMouseDown: false
    });
    if (this.state.mode === MODES.appending) {
      this.finishAppending();
      return;
    }
    if ([MODES.move, MODES.resize].includes(this.state.mode)) {
      this.finishEditing();
      return;
    }
  };
  finishEditing = () => {
    const {
      annotations
    } = this.state;
    // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
    this.setState({
      annotations,
      currentAnnotation: null,
      mode: MODES.select
    }, this.submit);
  };
  finishAppending = () => {
    if (!this.state.currentAnnotation) {
      return;
    }
    const annotations = this.getAllAnnotations();
    // setState is not guaranteed to be synchronous so we need
    // to use a callback to fire this.submit
    // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
    this.setState({
      annotations,
      currentAnnotation: null,
      mode: MODES.append
    }, this.submit);
  };
  updateScaleOnScroll = (newOffsets: CanvasOffset, newScale: number) => {
    this.setState({
      offsets: newOffsets,
      scale: newScale
    });
  };
  adjustOffset = (x: number, y: number) => {
    const offsets = {
      x: this.state.offsets.x + x,
      y: this.state.offsets.y + y
    };
    this.setState({
      offsets
    });
  };
  toggleGridMode = () => {
    const gridMode = (this.state.gridMode + 1) % GRID_MODES;
    this.setState({
      gridMode
    });
  };
  getAllAnnotations = () => {
    const currentAnnotations = this.state.currentAnnotation;
    if (currentAnnotations && currentAnnotations.meta) {
      currentAnnotations.meta.drawMode = this.state.appendMode;
      return [...this.state.annotations, currentAnnotations];
    } else {
      return this.state.annotations;
    }
  };
  getAnnotationsForCanvas() {
    const annotationsToDraw = this.getAllAnnotations().filter(annotation => {
      return !this.state.hiddenToolIds.includes(annotation.toolIndex);
    });
    return annotationsToDraw.map<any>((a, idx) => ({
      key: idx,
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      type: this.props.tools[a.toolIndex].type,
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      color: this.props.tools[a.toolIndex].color,
      x: a.meta.x,
      y: a.meta.y,
      height: a.meta.height,
      width: a.meta.width,
      lineType: a.isInitial === true ? LineType.SOLID : LineType.DASH,
      drawMode: a.meta.drawMode
    }));
  }
  renderCanvas() {
    const canvasStyle = {
      top: 0,
      left: TOOLBAR_WIDTH,
      position: 'absolute',
      cursor: this.getCursorType(),
      height: '100%',
      width: this.state.canvasWidth
    };
    const imageObj = {
      type: 'image',
      url: this.props.imageUrl || '',
      x: 0,
      y: 0,
      rotation: this.state.rotation
    };
    return (
      // @ts-expect-error todo(ts-migration) TS2322 Type '{ top: number; left: number; position: string; cursor: string; height: string; width: number; }' is not assignable to type 'CSSProperties'.
      <div style={canvasStyle}>
        <EditorCanvas scale={this.state.scale} offsets={this.state.offsets} objects={[imageObj, ...this.getAnnotationsForCanvas()]} onReferenceMouseDown={this.handleMouseDown} onReferenceMouseMove={this.handleMouseMove} onReferenceMouseUp={this.handleMouseUp} onUpdateImageSize={this.handleUpdateImageSize} onAnnotationClick={this.onAnnotationClick} onVertexClick={this.onVertexClick} onScrollScale={this.updateScaleOnScroll} selectedAnnotation={this.state.selectedAnnotation} gridEnabled={this.state.gridMode !== 0} gridColor={this.state.gridMode === 2 ? 'black' : 'gray'} />
      </div>
    );
  }
  handleToolClick = (index: number) => {
    this.handleShowToolClick(index);
    this.setState({
      selectedIndex: index
    });
  };
  handleHideToolClick = (index: number) => {
    const set = new Set(this.state.hiddenToolIds);
    if (index !== this.state.selectedIndex) {
      set.add(index);
    }
    this.setState({
      hiddenToolIds: Array.from(set)
    });
  };
  handleShowToolClick = (index: number) => {
    const set = new Set(this.state.hiddenToolIds);
    set.delete(index);
    this.setState({
      hiddenToolIds: Array.from(set)
    });
  };
  undo = () => {
    if (!this.state.annotations.length) {
      return;
    }
    const annotations = this.state.annotations.slice(0, -1);

    // setState is not guaranteed to be synchronous so we need
    // to use a callback to fire this.submit
    this.setState({
      annotations
    }, this.submit);
  };
  submit = () => {
    // get all annotations, covert them
    // WIP til we finalized the format
    const annotations = this.state.annotations.map((a, idx: number) => {
      const annotation = {
        ...a,
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        type: this.props.tools[a.toolIndex].type,
        meta: {
          ...a.meta,
          annotationType: 'V1'
        }
      };
      delete annotation.isInitial;
      return annotation;
    });
    const metaData = {
      imageSize: this.state.imgSize,
      rotation: this.state.rotation,
      invalidImage: this.state.invalidImage,
      labelValidation: this.state.labelValidation
    };

    // @ts-expect-error todo(ts-migration) TS2345 Argument of type '{ type: "boundingBox"; meta: { annotationType: string; x: number; y: number; width: number; height: number; drawMode?: string; }; toolIndex: number; categoryName: string; isInitial?:...
    this.props.onSubmit(annotations, metaData);
  };
  isAnnotationReachedMax(annotationIndex: number) {
    const selectedAnnotation = this.props.tools[annotationIndex];
    const count = this.state.annotations.filter(a => a.toolIndex === annotationIndex).length;
    // @ts-expect-error todo(ts-migration) TS18048 'selectedAnnotation' is possibly 'undefined'.
    return selectedAnnotation.max !== undefined ? count >= selectedAnnotation.max : false;
  }
  sidePanelToolsSelector() {
    return this.props.tools.map<any>((t, idx) => {
      const count = this.state.annotations.filter(a => a.toolIndex === idx).length;
      const hidden = this.state.hiddenToolIds.includes(idx);
      return {
        ...t,
        count,
        hidden
      };
    });
  }
  getCursorType() {
    if (this.state.mode === MODES.pan) {
      return this.state.isMouseDown ? 'grabbing' : 'grab';
    }
    if (this.state.mode === MODES.appending || this.state.mode === MODES.append) {
      return 'crosshair';
    }
    return 'pointer';
  }
  handleIconClick = (selectedMode: string) => {
    if (selectedMode === 'rotate') {
      this.handleRotate();
    } else if (selectedMode === 'undo') {
      this.undo();
    } else if (selectedMode === 'ruler') {
      this.toggleGridMode();
    } else if (Object.values(Labels).includes(selectedMode)) {
      // @ts-expect-error todo(ts-migration) TS2322 Type 'string' is not assignable to type '"select" | "move" | "resize" | "append" | "pan" | "appending"'.
      this.setState({
        mode: MODES.append,
        appendMode: selectedMode
      });
    } else {
      this.setState({
        mode: MODES[selectedMode]
      });
    }
  };
  onLabelValidation = ({
    isValid,
    reason
  }: {
    isValid: boolean;
    reason: string;
  }) => {
    const reasonIndex = this.state.labelValidation.reasons.indexOf(reason);
    let reasons = this.state.labelValidation.reasons;

    // remove the reason provided from existing reasons list
    if (reasonIndex >= 0) {
      reasons = [...this.state.labelValidation.reasons.slice(0, reasonIndex), ...this.state.labelValidation.reasons.slice(reasonIndex + 1)];
    }

    // if not isValid push the reason to existing reasons list
    if (isValid === false) {
      reasons = [...reasons, reason];
    }

    // TODO - move this.submit from all setState call backs and use componentDidUpdate
    this.setState({
      labelValidation: {
        isValid: reasons.length === 0,
        reasons
      }
    }, this.submit);
  };
  renderSidePanel() {
    const sidePanelStyle = {
      left: TOOLBAR_WIDTH + this.state.canvasWidth,
      position: 'absolute'
    };
    const {
      imageId
    } = this.props;
    const tools = this.sidePanelToolsSelector();
    return (
      // @ts-expect-error todo(ts-migration) TS2322 Type '{ left: number; position: string; }' is not assignable to type 'CSSProperties'.
      <div style={sidePanelStyle}>
        <Panel meta={imageId ? {
          id: imageId
        } : {}} width={PANEL_WIDTH} tools={tools} selectedIndex={this.state.selectedIndex} onToolClick={this.handleToolClick} onHideClick={this.handleHideToolClick} onShowClick={this.handleShowToolClick} invalidImage={this.state.invalidImage} labelValidation={this.state.labelValidation} onInvalidImageChange={val => this.setState({
          invalidImage: val
        }, this.submit)} onLabelValidation={this.onLabelValidation} />
      </div>
    );
  }
  render() {
    return <>
        <Container ref={this.containerRef}>
          <ToolbarContainer>
            <ToolBar iconList={[{
            type: 'undo'
          }, {
            type: 'divider'
          }, {
            type: MODES.select,
            isSelected: this.state.mode === MODES.select
          }, ...Object.values(Labels).filter(mode => this.state.selectedLabelTypes.includes(mode)).map(mode => ({
            // turning off flow type here as map is casting string to mixed
            type: mode,
            isSelected: this.state.mode === MODES.append && this.state.appendMode === mode
          })), {
            type: 'divider'
          }, {
            type: 'ruler'
          }, {
            type: 'rotate',
            disabled: !this.props.isRotateEnabled
          }]} onIconClick={this.handleIconClick}
          // @ts-expect-error todo(ts-migration) TS2322 Type '{ iconList: ({ type: string; isSelected: boolean; } | { type: string; } | { type: string; disabled: boolean; })[]; onIconClick: (selectedMode: string) => void; selectedType: "select" | "move" | ...
          selectedType={this.state.mode} />
          </ToolbarContainer>
          {this.renderCanvas()}
          {this.renderSidePanel()}
        </Container>

        <ToasterContainer placement={PLACEMENT.topRight} overrides={{
        Root: {
          style: {
            zIndex: 2
          }
        }
      }} />
      </>;
  }
}
export default ImageLabel;